import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useState } from 'react';
import {
  assumeEmergencyRequest,
  closeEmergencyRequest,
  EmergencyRequest,
  EmergencyRequestStatus,
  getEmergencyRequests,
} from 'src/apis/emergencyRequests';
import PageContainer from 'src/pages/components/PageContainer';
import { getUserId, isUserAdministrator, isUserPsychologist } from 'src/utils/auth';
import { Id } from 'src/utils/types';

const SOURCE_LABELS: Record<string, string> = {
  EEZYFAMILY: 'EezyFamily',
  CARE_CONNECT: 'Site Eezycare',
};

const STATUS_TABS: { label: string; value: EmergencyRequestStatus | 'ALL' }[] = [
  { label: 'Pendentes', value: 'PENDING' },
  { label: 'Assumidas', value: 'ASSUMED' },
  { label: 'Atendidas', value: 'ATTENDED' },
];

const isGuardOk = () => isUserPsychologist() || isUserAdministrator();

type RequestCardProps = {
  req: EmergencyRequest;
  currentUserId: Id;
  onAssume: (id: number) => void;
  onClose: (id: number) => void;
  loadingId: number | null;
};

const RequestCard: FC<RequestCardProps> = ({ req, currentUserId, onAssume, onClose, loadingId }) => {
  const isLoading = loadingId === req.id;
  const canAssume = req.status === 'PENDING';
  const canClose =
    req.status === 'ASSUMED' &&
    (isUserAdministrator() || String(req.fk_assigned_psychologist_id) === String(currentUserId));

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Typography fontWeight={600} fontSize={15}>
          {req.name}
        </Typography>
        <Chip
          size="small"
          label={SOURCE_LABELS[req.source] ?? req.source}
          variant="outlined"
          sx={{ fontSize: 11 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        <strong>WhatsApp:</strong>{' '}
        <a href={`https://wa.me/55${req.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noreferrer">
          {req.whatsapp}
        </a>
      </Typography>

      <Typography variant="body2" color="text.secondary">
        <strong>Mensagem:</strong> {req.message}
      </Typography>

      {req.assigned_psychologist && (
        <Typography variant="body2" color="text.secondary">
          <strong>Responsável:</strong> {req.assigned_psychologist.name}
        </Typography>
      )}

      <Typography variant="caption" color="text.disabled">
        Recebida em{' '}
        {new Date(req.created_at).toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </Typography>

      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
        {canAssume && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={isLoading}
            onClick={() => onAssume(req.id)}
            startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Assumir atendimento
          </Button>
        )}
        {canClose && (
          <Button
            size="small"
            variant="outlined"
            color="success"
            disabled={isLoading}
            onClick={() => onClose(req.id)}
            startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Marcar como atendido
          </Button>
        )}
      </Box>
    </Box>
  );
};

const EmergencyRequests: FC = (): JSX.Element => {
  const [tab, setTab] = useState<EmergencyRequestStatus>('PENDING');
  const [requests, setRequests] = useState<EmergencyRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingId, setLoadingId] = useState<number | null>(null);
  const currentUserId = getUserId();

  const fetchRequests = useCallback(async (status: EmergencyRequestStatus) => {
    setLoading(true);
    setError('');
    try {
      const data = await getEmergencyRequests(status);
      setRequests(data);
    } catch {
      setError('Erro ao carregar solicitações. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests(tab);
  }, [tab, fetchRequests]);

  const handleAssume = async (id: number) => {
    setLoadingId(id);
    try {
      await assumeEmergencyRequest(id);
      await fetchRequests(tab);
    } catch {
      setError('Não foi possível assumir a solicitação. Ela pode ter sido assumida por outro profissional.');
    } finally {
      setLoadingId(null);
    }
  };

  const handleClose = async (id: number) => {
    setLoadingId(id);
    try {
      await closeEmergencyRequest(id);
      await fetchRequests(tab);
    } catch {
      setError('Não foi possível encerrar a solicitação. Tente novamente.');
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <PageContainer title="Solicitações Emergenciais" error={error} guard={isGuardOk}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v as EmergencyRequestStatus)}
        sx={{ mb: 2 }}
      >
        {STATUS_TABS.map((t) => (
          <Tab key={t.value} label={t.label} value={t.value} />
        ))}
      </Tabs>

      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box display="flex" flexDirection="column" gap={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} />
          ))}
        </Box>
      ) : requests.length === 0 ? (
        <Alert severity="info">Nenhuma solicitação {tab === 'PENDING' ? 'pendente' : tab === 'ASSUMED' ? 'assumida' : 'atendida'} no momento.</Alert>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {requests.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              currentUserId={currentUserId}
              onAssume={handleAssume}
              onClose={handleClose}
              loadingId={loadingId}
            />
          ))}
        </Box>
      )}
    </PageContainer>
  );
};

export default EmergencyRequests;
